import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import { orpc } from "@/frontend/orpc-client";
import { MAX_UPLOAD_BYTES } from "@/frontend/constants/synthetic-hospital";
import type { UploadPatientDialogProps } from "@/frontend/types/synthetic-hospital";

/** Uploads one FHIR R4 Bundle and hands the resulting sample patient to onPick. */
export function UploadPatientDialog({ open, onOpenChange, onPick }: UploadPatientDialogProps) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const uploadMutation = useMutation(orpc.syntheticHospital.uploadSamplePatient.mutationOptions());
  const isUploading = uploadMutation.isPending;

  /** Blocks close while an upload is in flight. */
  function handleOpenChange(nextOpen: boolean) {
    if (isUploading) return;
    if (!nextOpen) setError(null);
    onOpenChange(nextOpen);
  }

  /** Client-side validates, uploads, and hands the sample patient to onPick. Rejects non-JSON, empty, or over-sized files inline. */
  async function handleFile(file: File) {
    setError(null);
    const isJson = file.name.toLowerCase().endsWith(".json") || /\bjson\b/i.test(file.type);
    if (!isJson) { setError("Choose a FHIR R4 Bundle JSON file."); return; }
    if (file.size === 0) { setError("File is empty."); return; }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`File is over ${MAX_UPLOAD_BYTES / 1_000_000} MB.`);
      return;
    }
    try {
      const bundleJson = await file.text();
      const result = await uploadMutation.mutateAsync({ bundleJson });
      queryClient.invalidateQueries({ queryKey: orpc.syntheticHospital.listSamplePatients.key() });
      onPick?.(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    }
  }

  /** Clears the input value on each pick so re-selecting the same file re-fires change. */
  function onFilePicked(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) void handleFile(file);
    event.target.value = "";
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" showCloseButton={!isUploading}>
        <DialogHeader>
          <DialogTitle>Add your own patient</DialogTitle>
          <DialogDescription>
            Upload a FHIR bundle and use it as a synthetic patient
          </DialogDescription>
        </DialogHeader>

        <div
          onClick={() => fileRef.current?.click()}
          className="rounded-lg border border-dashed p-8 text-center cursor-pointer hover:border-foreground/40 transition-colors"
        >
          <Upload className="size-5 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm">
            <span className="text-foreground font-medium">Choose a file</span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            FHIR R4 Bundle JSON, up to {MAX_UPLOAD_BYTES / 1_000_000} MB.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={onFilePicked}
          />
        </div>

        {isUploading && <p className="text-xs text-muted-foreground">Uploading…</p>}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </DialogContent>
    </Dialog>
  );
}
